/**
 * SceneNameInput — the one editable scene name (spec 018).
 *
 * Used by the editor header (full and embedded toolbars) and the Scene
 * properties panel. Typing edits a local draft; Enter or blur commits through
 * `sceneStore.renameScene` (trimmed, at most 255 characters, an empty name
 * reverts), Escape reverts. A committed rename is one document change.
 */

import React, { useRef, useState } from 'react';
import { useSceneStore, SCENE_NAME_MAX_LENGTH } from '../../stores/sceneStore';

interface SceneNameInputProps {
  className?: string;
  placeholder?: string;
  'aria-label'?: string;
  'data-testid'?: string;
}

export const SceneNameInput: React.FC<SceneNameInputProps> = ({
  className,
  placeholder,
  'aria-label': ariaLabel = 'Scene name',
  'data-testid': testId,
}) => {
  const name = useSceneStore((state) => state.scene?.name ?? '');
  const renameScene = useSceneStore((state) => state.renameScene);
  // null while not editing: the input shows the stored name
  const [draft, setDraftState] = useState<string | null>(null);
  const draftRef = useRef<string | null>(null);

  const setDraft = (value: string | null) => {
    draftRef.current = value;
    setDraftState(value);
  };

  const commit = () => {
    const value = draftRef.current;
    setDraft(null);
    if (value !== null) renameScene(value);
  };

  return (
    <input
      type="text"
      className={className}
      value={draft ?? name}
      maxLength={SCENE_NAME_MAX_LENGTH}
      placeholder={placeholder}
      aria-label={ariaLabel}
      data-testid={testId}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commit();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          e.stopPropagation();
          setDraft(null);
        }
      }}
    />
  );
};

export default SceneNameInput;
